import { ReactNode } from "react";
import styled from "styled-components";
import { designSystemVars as vars } from "../tokens";
import { ButtonBase } from "./Button";

export interface ViewToggleItem {
  value: string;
  label: string;
  icon: ReactNode;
}

export interface ViewToggleProps {
  items: ViewToggleItem[];
  value: string;
  onValueChange: (value: string) => void;
  "aria-label": string;
  className?: string;
}

const ToggleGroup = styled.div`
  display: flex;
  width: fit-content;
  padding: ${vars.spacing.xs};
  align-items: center;
  gap: ${vars.spacing.sm};
  border-radius: ${vars.radius.md};
  border: ${vars.borderWidth.hairline} solid ${vars.color.border.medium};
  background: ${vars.color.background.subtle};
`;

const ToggleButton = styled(ButtonBase)<{ $selected: boolean }>`
  display: flex;
  padding: ${vars.spacing.sm};
  justify-content: center;
  align-items: center;
  gap: ${vars.spacing.sm};
  border-radius: ${vars.radius.sm};
  border: ${vars.spacing.none} solid ${vars.color.border.transparent};
  background: ${({ $selected }) =>
    $selected
      ? vars.color.action.primaryHoverStrong
      : vars.color.background.transparent};
  color: ${({ $selected }) =>
    $selected ? vars.color.text.inverse : vars.color.action.primary};
  cursor: default;
  transition:
    background ${vars.motion.normal} ${vars.motion.easing},
    color ${vars.motion.normal} ${vars.motion.easing};

  &:hover {
    background: ${vars.color.action.primaryHoverStrong};
    color: ${vars.color.text.inverse};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${vars.shadow.focus};
  }

  > span {
    display: flex;
  }

  svg {
    width: ${vars.spacing.xl};
    height: ${vars.spacing.xl};
  }
`;

export const ViewToggle = ({
  items,
  value,
  onValueChange,
  className,
  "aria-label": ariaLabel,
}: ViewToggleProps) => (
  <ToggleGroup role="group" aria-label={ariaLabel} className={className}>
    {items.map((item) => (
      <ToggleButton
        key={item.value}
        type="button"
        aria-label={item.label}
        title={item.label}
        aria-pressed={item.value === value}
        $selected={item.value === value}
        onClick={() => onValueChange(item.value)}
      >
        <span aria-hidden="true">{item.icon}</span>
      </ToggleButton>
    ))}
  </ToggleGroup>
);
